import React from "react";
import { LppPlan, Mission, SalaryDeductionType, SalaryType } from "../../../__generated__/graphql";
import { GetSalaryDetailsQuery } from "../../../__generated__/operations/graphql";
import { useI18n } from "../../../hooks/i18n-hooks";
import { useLazyQuery } from "@apollo/client/react";
import { GET_SALARY_DETAILS } from "../../../graphql/salaries";
import { STANDARD_WORKING_TIME_CLASSIC_SPEC } from "../../working_time/helpers";
import { useApolloErrorSnackbar } from "../../../hooks/snackbar";
import { useCatchLazyQueryError } from "../../../hooks/errors";
import { toISODateString } from "../../../hooks/date";
import { getIsPay13thSalaryWithSalary } from "../../../utils";

export const useSalaryTypes = () => {
    const i18n = useI18n();
    const salaryType = React.useMemo(
        () => [
            {
                label: i18n.t("screens.user.create_mission_salary.salaryType.gross"),
                value: SalaryType.Gross,
            },
            {
                label: i18n.t("screens.user.create_mission_salary.salaryType.net"),
                value: SalaryType.Net,
            },
            {
                label: i18n.t("screens.user.create_mission_salary.salaryType.invoice"),
                value: SalaryType.Invoice,
            },
        ],
        [i18n],
    );
    return salaryType;
};

export type Salaries = {
    requestedSalaryType: SalaryType;
    invoiceSalary: number;
    grossSalary: number;
    netSalary: number;
    deferredIndemnity: number;
    // What the worker's projection of a 13x contract still being created needs (SalaryProjections).
    thirteenMonthCompensation?: number;
    workerDeductions?: SalaryDeductionType[] | null;
    baseSalary: number;
    minBaseSalary: number;
    minGrossSalary: number;
    minNetSalary: number;
    minInvoiceSalary: number;
};

export const useComputeSalary = (
    mission: Mission,
    ijmGroupId: string | null | undefined,
    lppPlan?: LppPlan | null,
    setSalaries?: (salaries: Salaries | null) => void,
    setVacationdays?: (vacationDays: number) => void,
    setHas13thSalary?: (has13thSalary: boolean) => void,
    setLppPlan?: (lppPlan: LppPlan | null) => void,
    setPay13thSalaryWithSalary?: (value: boolean) => void,
) => {
    const i18n = useI18n();
    const [getSalaryDetails, { error: getSalaryDetailsError }] = useLazyQuery(GET_SALARY_DETAILS);
    useApolloErrorSnackbar(getSalaryDetailsError);
    const catchLazyQueryError = useCatchLazyQueryError();
    const [salaryDetails, setSalaryDetails] = React.useState<GetSalaryDetailsQuery | null>(null);
    const [loading, setloading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const computeSalary = React.useCallback(
        async (salary: number, salaryType: SalaryType, startDate?: Date, endDate?: Date) => {
            if (!salary || salary < 0.02 || ijmGroupId === undefined || lppPlan === undefined) {
                setSalaryDetails(null);
                setSalaries?.(null);
                return;
            }
            setloading(true);
            setError(null);
            try {
                if (mission.currentContract?.cctEditionId && mission.workerInfo?.birthdate) {
                    try {
                        const result = await getSalaryDetails({
                            variables: {
                                cctEditionId: mission.currentContract.cctEditionId,
                                cctDetailsValues:
                                    mission.currentContract.cctDetails?.tempdataCallValues,
                                workerProfileId: mission.workerInfo.id,
                                requestedSalary: Math.round(salary * 100),
                                requestedSalaryType: salaryType,
                                vacationDays: mission.currentContract.vacationDays,
                                has13thSalary: mission.currentContract.has13thSalary,
                                earningType: mission.currentContract.earningType,
                                workingTimeClassicSpec:
                                    mission.currentContract.workingTimeClassicSpec ||
                                    STANDARD_WORKING_TIME_CLASSIC_SPEC,
                                hoursPerWeekFullTime:
                                    mission.currentContract.hoursPerWeekFullTime?.toString(),
                                activityTypeId: mission.activityType?.id,
                                ijmGroupId: ijmGroupId,
                                workerProfileInfo: {
                                    birthdate: mission.workerInfo.birthdate,
                                    gender: mission.workerInfo.gender,
                                },
                                missionStateCode:
                                    mission.currentContract?.workingAddress?.locality?.state?.code,
                                legalEmployerId: mission.legalEmployer?.id,
                                startDate:
                                    toISODateString(startDate) || mission.currentContract.startDate,
                                endDate:
                                    toISODateString(endDate) || mission.currentContract.endDate,
                                lppPlanId: lppPlan?.id || null,
                                disableAvsSmallSalaryExemption:
                                    mission.currentContract.disableAvsSmallSalaryExemption || false,
                                forceLpp: !!mission.currentContract.forceLpp,
                                payVacationIndemnityWithSalary:
                                    !!mission.currentContract.payVacationIndemnityWithSalary,
                                pay13thSalaryWithSalary: getIsPay13thSalaryWithSalary({
                                    earningType: mission.currentContract.earningType,
                                    has13thSalary: mission.currentContract.has13thSalary,
                                    pay13thSalaryWithSalary:
                                        mission.currentContract.pay13thSalaryWithSalary,
                                }),
                            },
                        });
                        if (result.error) {
                            setError(result.error.message);
                        }
                        if (result.data) {
                            setSalaries?.({
                                requestedSalaryType:
                                    result.data.getSalaryDetails.requestedSalaryType,
                                invoiceSalary: result.data.getSalaryDetails.invoiceSalary,
                                grossSalary: result.data.getSalaryDetails.grossSalary,
                                netSalary: result.data.getSalaryDetails.netSalary,
                                deferredIndemnity: result.data.getSalaryDetails.deferredIndemnity,
                                thirteenMonthCompensation:
                                    result.data.getSalaryDetails.thirteenMonthCompensation,
                                workerDeductions: result.data.getSalaryDetails.workerDeductions,
                                baseSalary: result.data.getSalaryDetails.baseSalary,
                                minBaseSalary: result.data.getSalaryDetails.minBaseSalary,
                                minGrossSalary: result.data.getSalaryDetails.minGrossSalary,
                                minNetSalary: result.data.getSalaryDetails.minNetSalary,
                                minInvoiceSalary: result.data.getSalaryDetails.minInvoiceSalary,
                            });
                            setVacationdays?.(result.data.getSalaryDetails.vacationDays);
                            setHas13thSalary?.(result.data.getSalaryDetails.has13thSalary);
                            setPay13thSalaryWithSalary?.(
                                result.data.getSalaryDetails.pay13thSalaryWithSalary,
                            );
                            setLppPlan?.(lppPlan);
                            setSalaryDetails(result.data);
                        }
                    } catch (e: unknown) {
                        catchLazyQueryError(e as Error);
                    }
                } else {
                    setError(
                        i18n.t("screens.user.create_mission_salary.compute_salary_missing_data"),
                    );
                }
            } finally {
                setloading(false);
            }
        },
        [
            setSalaries,
            mission.activityType?.id,
            mission.currentContract?.startDate,
            mission.currentContract?.endDate,
            mission.currentContract?.cctEditionId,
            mission.currentContract?.cctDetails?.tempdataCallValues,
            mission.currentContract?.vacationDays,
            mission.currentContract?.has13thSalary,
            mission.currentContract?.earningType,
            mission.currentContract?.workingTimeClassicSpec,
            mission.currentContract?.hoursPerWeekFullTime,
            mission.currentContract?.disableAvsSmallSalaryExemption,
            mission.currentContract?.forceLpp,
            mission.currentContract?.payVacationIndemnityWithSalary,
            mission.currentContract?.pay13thSalaryWithSalary,
            mission.currentContract?.workingAddress?.locality?.state?.code,
            mission.legalEmployer?.id,
            mission.workerInfo?.birthdate,
            mission.workerInfo.id,
            mission.workerInfo.gender,
            getSalaryDetails,
            ijmGroupId,
            setVacationdays,
            setHas13thSalary,
            setPay13thSalaryWithSalary,
            i18n,
            catchLazyQueryError,
            lppPlan,
            setLppPlan,
        ],
    );

    return {
        computeSalary,
        salaryDetails,
        loading,
        error,
    };
};
